/**
 * useGlobalKeys — THE app-level keyboard-shortcut surface.
 *
 * One window `keydown` listener for every global binding (graph-editor keys +
 * surface toggles + file ops). House rule: don't scatter new
 * `window.addEventListener('keydown', …)` effects — add the binding here and
 * pass a handler from the owning component.
 *
 * Guards applied centrally:
 * - **Typing contexts are untouchable.** Events originating in inputs,
 *   textareas, contenteditable, Monaco (its editing surface is a textarea),
 *   and CodeMirror/Jupyter cells never reach the bindings — those surfaces own
 *   their own keys (Monaco keeps its Ctrl+S/Ctrl+Z/etc).
 * - **IME composition** (`e.isComposing`) is skipped.
 * - **Toggles ignore `e.repeat`** (holding a key must not flicker a modal);
 *   undo/redo/delete auto-repeat by design.
 * - **Overlays suppress non-global bindings**: while a modal/dialog/menu owns
 *   the screen, graph + file bindings sleep (their surface isn't active), but
 *   the global toggles stay live so a shortcut can also close what it opened.
 *
 * `handlers`/`opts` are read through refs (never in the effect deps), so the
 * owning component passes fresh closures every render without re-subscribing.
 */
import { useEffect, useRef } from 'react';

export interface GlobalKeyHandlers {
  // ── Global toggles (active in every view, even under overlays) ──
  toggleAsk?: () => void;
  toggleChat?: () => void;
  openLibrary?: () => void;
  // ── File ops (gated on `fileKeys` — not in the Code tab) ──
  save?: () => void;
  saveAs?: () => void;
  openFile?: () => void;
  // ── Graph-editor bindings (gated on `graphKeys`) ──
  undo?: () => void;
  redo?: () => void;
  deleteSelection?: () => void;
  duplicateSelection?: () => void;
  copySelection?: () => void;
  cutSelection?: () => void;
  paste?: () => void;
  selectAll?: () => void;
  openSelected?: () => void;
  clearSelection?: () => void;
  fitView?: () => void;
  zoomIn?: () => void;
  zoomOut?: () => void;
  // ── Replay bindings (gated on `replayKeys` — a replay tab owns the
  //    canvas; arrow keys step recorded ticks, Space toggles playback) ──
  replayStep?: (delta: number) => void;
  replayTogglePlay?: () => void;
}

export interface UseGlobalKeysOpts {
  /** A modal/dialog/menu overlay owns the screen → suppress graph + file
   *  bindings (global toggles stay live). In addition to this flag, any
   *  `[aria-modal="true"]` element in the DOM suppresses them too — that
   *  catches the provider-owned Confirm/Prompt dialogs whose pending state
   *  the owning component can't read, and it's evaluated at event time so a
   *  dialog opened mid-render is respected. */
  overlayOpen?: boolean;
  /** Graph bindings enabled (View tab with an editable session). */
  graphKeys?: boolean;
  /** File-op bindings enabled (editable session, NOT the Code tab — Monaco
   *  owns Ctrl+S there). */
  fileKeys?: boolean;
  /** Replay bindings enabled (View tab with an active replay tab — the
   *  scrubber owns ←/→/Shift+←/→/Space). Mutually exclusive with
   *  `graphKeys` in practice: graph edits target the live bundle. */
  replayKeys?: boolean;
}

/** True when the event originated somewhere the user is typing text — the
 *  binding layer must not touch those keystrokes. */
export function isTypingTarget(e: KeyboardEvent): boolean {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // Focus parked on editor chrome rather than the surface itself.
  return !!t.closest('.monaco-editor, .cm-editor, [contenteditable="true"], [role="textbox"]');
}

export function useGlobalKeys(handlers: GlobalKeyHandlers, opts: UseGlobalKeysOpts = {}): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing || isTypingTarget(e)) return;
      const h = handlersRef.current;
      const o = optsRef.current;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();

      /** Run the handler (preventDefault so the browser default — the Save
       *  dialog, the browser file picker, find-in-page — never fires).
       *  Auto-repeat is passed through only where holding the key should
       *  repeat the action (undo/redo/delete). */
      const fire = (fn: (() => void) | undefined, repeat = false): boolean => {
        if (!fn) return false;
        if (e.repeat && !repeat) return false;
        e.preventDefault();
        fn();
        return true;
      };

      // ── Global toggles — before the overlay guard so a shortcut can close
      //    the overlay it opened (e.g. Ctrl+/ toggling chat closed).
      if (mod && !e.altKey) {
        if (!e.shiftKey && k === 'k' && fire(h.toggleAsk)) return;
        if (!e.shiftKey && k === '/' && fire(h.toggleChat)) return;
        if (e.shiftKey && k === 'p' && fire(h.openLibrary)) return;
      }

      // Everything below sleeps while an overlay owns the screen (the App's
      // flags + any open aria-modal dialog — see UseGlobalKeysOpts.overlayOpen).
      if (o.overlayOpen || document.querySelector('[aria-modal="true"]')) return;

      // ── File ops.
      if (mod && !e.altKey && o.fileKeys) {
        if (!e.shiftKey && k === 's' && fire(h.save)) return;
        if (e.shiftKey && k === 's' && fire(h.saveAs)) return;
        if (!e.shiftKey && k === 'o' && fire(h.openFile)) return;
      }

      // ── Replay bindings (the active replay tab's scrubber). Plain keys —
      //    no modifiers — and Space must not scroll the page.
      if (o.replayKeys && !mod && !e.altKey) {
        if (k === 'arrowleft') {
          e.preventDefault();
          h.replayStep?.(e.shiftKey ? -10 : -1);
          return;
        }
        if (k === 'arrowright') {
          e.preventDefault();
          h.replayStep?.(e.shiftKey ? 10 : 1);
          return;
        }
        if (k === ' ' && fire(h.replayTogglePlay)) return;
      }

      // ── Graph-editor bindings.
      if (o.graphKeys) {
        if (mod && !e.altKey) {
          if (!e.shiftKey && k === 'z' && fire(h.undo, /* repeat */ true)) return;
          if (e.shiftKey && k === 'z' && fire(h.redo, /* repeat */ true)) return;
          if (!e.shiftKey && k === 'y' && fire(h.redo, /* repeat */ true)) return;
          if (k === 'd' && fire(h.duplicateSelection)) return;
          if (k === 'c' && fire(h.copySelection)) return;
          if (k === 'x' && fire(h.cutSelection)) return;
          if (k === 'v' && fire(h.paste)) return;
          if (k === 'a' && fire(h.selectAll)) return;
          if (k === '0' && fire(h.fitView)) return;
          if ((k === '=' || k === '+') && fire(h.zoomIn)) return;
          if (k === '-' && fire(h.zoomOut)) return;
        } else if (!mod && !e.altKey && !e.shiftKey) {
          if ((k === 'delete' || k === 'backspace') && fire(h.deleteSelection, /* repeat */ true)) return;
          if (k === 'enter' && fire(h.openSelected)) return;
        }
        if (!mod && !e.altKey && k === 'escape' && fire(h.clearSelection)) return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
